import React from 'react';

/* ━━━ 1. Gauge Meter ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface GaugeMeterProps {
  highlightTarget?: string;
  labels?: Record<string, string>;
  accentColor?: string;
  opacity?: number;
  value?: number; // 0-1
}
export const GaugeMeter: React.FC<GaugeMeterProps> = ({
  highlightTarget, labels = { low: 'Low', mid: 'Medium', high: 'High', title: 'Level' },
  accentColor = '#10b981', opacity = 1, value = 0.6,
}) => {
  const cx = 400, cy = 300, r = 160;
  const startAngle = Math.PI * 0.8, endAngle = Math.PI * 0.2;
  const sweep = 2 * Math.PI - (startAngle - endAngle);
  const needleAngle = startAngle - value * sweep;
  const nx = cx + (r - 30) * Math.cos(needleAngle);
  const ny = cy - (r - 30) * Math.sin(needleAngle);
  const hi = highlightTarget === 'needle';
  // Arc path helper
  const arc = (startA: number, endA: number, radius: number) => {
    const x1 = cx + radius * Math.cos(Math.PI - startA);
    const y1 = cy - radius * Math.sin(Math.PI - startA);
    const x2 = cx + radius * Math.cos(Math.PI - endA);
    const y2 = cy - radius * Math.sin(Math.PI - endA);
    return `M${x1},${y1} A${radius},${radius} 0 1,1 ${x2},${y2}`;
  };
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="gm-arc" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#ef4444"/><stop offset="50%" stopColor="#f59e0b"/><stop offset="100%" stopColor="#22c55e"/></linearGradient>
        <filter id="gm-sh"><feDropShadow dx="2" dy="3" stdDeviation="4" floodOpacity="0.35"/></filter>
        <radialGradient id="gm-hub" cx="40%" cy="35%"><stop offset="0%" stopColor="#94a3b8"/><stop offset="100%" stopColor="#334155"/></radialGradient>
      </defs>
      {/* Gauge track shadow */}
      <path d={arc(startAngle, endAngle, r)} fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="24" strokeLinecap="round" transform="translate(3,4)"/>
      {/* Gauge track */}
      <path d={arc(startAngle, endAngle, r)} fill="none" stroke="#1e293b" strokeWidth="22" strokeLinecap="round" filter="url(#gm-sh)"/>
      {/* Colored fill */}
      <path d={arc(startAngle, startAngle - value * sweep, r)} fill="none" stroke="url(#gm-arc)" strokeWidth="20" strokeLinecap="round"/>
      {/* Tick marks */}
      {Array.from({length: 11}, (_, i) => {
        const pct = i / 10;
        const a = Math.PI - (startAngle - pct * sweep);
        const tx1 = cx + (r + 8) * Math.cos(a), ty1 = cy - (r + 8) * Math.sin(a);
        const tx2 = cx + (r + 18) * Math.cos(a), ty2 = cy - (r + 18) * Math.sin(a);
        return <line key={i} x1={tx1} y1={ty1} x2={tx2} y2={ty2} stroke="#475569" strokeWidth="2"/>;
      })}
      {/* Needle */}
      <line x1={cx+1} y1={cy+2} x2={nx+1} y2={ny+2} stroke="rgba(0,0,0,0.3)" strokeWidth="4"/>
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={hi?'#fff':'#e2e8f0'} strokeWidth="3" strokeLinecap="round"/>
      {/* Hub */}
      <circle cx={cx+2} cy={cy+3} r="14" fill="rgba(0,0,0,0.3)"/>
      <circle cx={cx} cy={cy} r="14" fill="url(#gm-hub)"/>
      <circle cx={cx-4} cy={cy-5} r="3" fill="rgba(255,255,255,0.3)"/>
      {/* Labels */}
      <text x={cx - r + 20} y={cy + 40} fill="#94a3b8" fontSize="10" fontFamily="Inter,sans-serif">{labels.low||'Low'}</text>
      <text x={cx} y={cy - r + 30} textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="Inter,sans-serif">{labels.mid||'Mid'}</text>
      <text x={cx + r - 20} y={cy + 40} textAnchor="end" fill="#94a3b8" fontSize="10" fontFamily="Inter,sans-serif">{labels.high||'High'}</text>
      <text x={cx} y={cy + 50} textAnchor="middle" fill="#e2e8f0" fontSize="14" fontFamily="Inter,sans-serif" fontWeight="700">{labels.title||'Level'}</text>
      <text x={cx} y={cy + 70} textAnchor="middle" fill={accentColor} fontSize="20" fontFamily="Inter,sans-serif" fontWeight="700">{Math.round(value*100)}%</text>
    </g>
  );
};

/* ━━━ 2. Thermometer Bar ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface ThermometerBarProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  value?: number;
}
export const ThermometerBar: React.FC<ThermometerBarProps> = ({
  highlightTarget, labels = ['Cold','Warm','Hot'],
  accentColor = '#ef4444', opacity = 1, value = 0.65,
}) => {
  const cx = 400, topY = 60, botY = 400, tubeW = 36, bulbR = 32;
  const fillY = botY - (botY - topY) * value;
  const hi = highlightTarget === 'level';
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="th-fill" x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stopColor={accentColor}/><stop offset="100%" stopColor="#fbbf24"/></linearGradient>
        <linearGradient id="th-tube" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#334155"/><stop offset="50%" stopColor="#475569"/><stop offset="100%" stopColor="#1e293b"/></linearGradient>
        <filter id="th-sh"><feDropShadow dx="3" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {/* Tube shadow */}
      <rect x={cx-tubeW/2+3} y={topY+4} width={tubeW} height={botY-topY} rx={tubeW/2} fill="rgba(0,0,0,0.3)"/>
      {/* Tube */}
      <rect x={cx-tubeW/2} y={topY} width={tubeW} height={botY-topY} rx={tubeW/2} fill="url(#th-tube)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" filter="url(#th-sh)"/>
      {/* Fill */}
      <rect x={cx-tubeW/2+4} y={fillY} width={tubeW-8} height={botY-fillY} rx={(tubeW-8)/2} fill="url(#th-fill)"/>
      {/* Specular highlight */}
      <line x1={cx-tubeW/2+6} y1={topY+10} x2={cx-tubeW/2+6} y2={botY-10} stroke="rgba(255,255,255,0.12)" strokeWidth="2"/>
      {/* Bulb */}
      <circle cx={cx+3} cy={botY+bulbR+4} r={bulbR} fill="rgba(0,0,0,0.3)"/>
      <circle cx={cx} cy={botY+bulbR} r={bulbR} fill={accentColor} opacity="0.8"/>
      <circle cx={cx-10} cy={botY+bulbR-10} r="6" fill="rgba(255,255,255,0.3)"/>
      {/* Scale labels */}
      {labels.map((label, i) => {
        const pct = i / (labels.length - 1);
        const y = botY - (botY - topY) * pct;
        return <g key={i}>
          <line x1={cx+tubeW/2+4} y1={y} x2={cx+tubeW/2+14} y2={y} stroke="#475569" strokeWidth="2"/>
          <text x={cx+tubeW/2+20} y={y+4} fill="#94a3b8" fontSize="10" fontFamily="Inter,sans-serif">{label}</text>
        </g>;
      })}
      {hi && <rect x={cx-tubeW/2-6} y={fillY-4} width={tubeW+12} height={botY-fillY+12} rx="20" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></rect>}
    </g>
  );
};

/* ━━━ 3. Microscope View ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface MicroscopeViewProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
}
export const MicroscopeView: React.FC<MicroscopeViewProps> = ({
  highlightTarget, labels = ['Specimen A','Specimen B','Specimen C'],
  accentColor = '#06b6d4', opacity = 1,
}) => {
  const cx = 400, cy = 260, r = 170;
  // Random-ish specimen positions inside the circle
  const specimens = [
    { x: cx - 50, y: cy - 40, size: 28 },
    { x: cx + 60, y: cy + 20, size: 22 },
    { x: cx - 20, y: cy + 65, size: 18 },
  ];
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="mv-bg" cx="50%" cy="50%"><stop offset="0%" stopColor="#1a2744"/><stop offset="85%" stopColor="#0c1425"/><stop offset="100%" stopColor="#000"/></radialGradient>
        <radialGradient id="mv-spec" cx="35%" cy="30%"><stop offset="0%" stopColor="#e2e8f0"/><stop offset="100%" stopColor={accentColor}/></radialGradient>
        <clipPath id="mv-clip"><circle cx={cx} cy={cy} r={r}/></clipPath>
        <filter id="mv-sh"><feDropShadow dx="2" dy="3" stdDeviation="4" floodOpacity="0.4"/></filter>
      </defs>
      {/* Viewport ring */}
      <circle cx={cx+3} cy={cy+4} r={r+6} fill="rgba(0,0,0,0.3)"/>
      <circle cx={cx} cy={cy} r={r+6} fill="none" stroke="#475569" strokeWidth="10" filter="url(#mv-sh)"/>
      <circle cx={cx} cy={cy} r={r+2} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
      {/* Viewport background */}
      <circle cx={cx} cy={cy} r={r} fill="url(#mv-bg)"/>
      {/* Grid lines */}
      <g clipPath="url(#mv-clip)" opacity="0.1">
        {Array.from({length: 11}, (_, i) => {
          const pos = cx - r + i * (2*r/10);
          return <g key={i}><line x1={pos} y1={cy-r} x2={pos} y2={cy+r} stroke="#fff" strokeWidth="0.5"/><line x1={cx-r} y1={pos-cx+cy} x2={cx+r} y2={pos-cx+cy} stroke="#fff" strokeWidth="0.5"/></g>;
        })}
      </g>
      {/* Specimens */}
      <g clipPath="url(#mv-clip)">
        {specimens.slice(0, labels.length).map((s, i) => {
          const id = `specimen_${i+1}`;
          const hi = highlightTarget === id;
          return <g key={i}>
            <circle cx={s.x+1} cy={s.y+2} r={s.size} fill="rgba(0,0,0,0.3)"/>
            <circle cx={s.x} cy={s.y} r={s.size} fill="url(#mv-spec)" opacity={hi?0.9:0.5} stroke={hi?'#fff':'none'} strokeWidth="2"/>
            <circle cx={s.x - s.size*0.3} cy={s.y - s.size*0.3} r={s.size*0.2} fill="rgba(255,255,255,0.35)"/>
            <text x={s.x} y={s.y + s.size + 14} textAnchor="middle" fill="#e2e8f0" fontSize="9" fontFamily="Inter,sans-serif">{labels[i]}</text>
            {hi && <circle cx={s.x} cy={s.y} r={s.size+10} fill="none" stroke="#fff" strokeWidth="1.5" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></circle>}
          </g>;
        })}
      </g>
      {/* Crosshairs */}
      <line x1={cx} y1={cy-r+10} x2={cx} y2={cy+r-10} stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
      <line x1={cx-r+10} y1={cy} x2={cx+r-10} y2={cy} stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
      {/* Magnification label */}
      <text x={cx+r-30} y={cy+r-10} textAnchor="end" fill="#475569" fontSize="10" fontFamily="monospace">40x</text>
    </g>
  );
};

/* ━━━ 4. Chemical Equation ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface ChemicalEquationProps {
  highlightTarget?: string;
  labels?: Record<string, string>;
  reactants?: string[];
  products?: string[];
  accentColor?: string;
  opacity?: number;
}
export const ChemicalEquation: React.FC<ChemicalEquationProps> = ({
  highlightTarget, reactants = ['H₂','O₂'], products = ['H₂O'],
  accentColor = '#f59e0b', opacity = 1,
}) => {
  const cy = 260, leftX = 150, rightX = 650, arrowX = 400;
  const rGap = (arrowX - leftX - 40) / reactants.length;
  const pGap = (rightX - arrowX - 40) / products.length;
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="ce-mol" cx="35%" cy="30%"><stop offset="0%" stopColor="#e2e8f0"/><stop offset="100%" stopColor={accentColor}/></radialGradient>
        <filter id="ce-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {/* Reactants */}
      {reactants.map((r, i) => {
        const x = leftX + i * rGap + rGap / 2;
        const id = `reactant_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={`r${i}`} filter="url(#ce-sh)">
          <circle cx={x+2} cy={cy+3} r="36" fill="rgba(0,0,0,0.3)"/>
          <circle cx={x} cy={cy} r="36" fill="url(#ce-mol)" stroke={hi?'#fff':accentColor} strokeWidth={hi?3:1.5}/>
          <circle cx={x-10} cy={cy-12} r="6" fill="rgba(255,255,255,0.35)"/>
          <text x={x} y={cy+6} textAnchor="middle" fill="#fff" fontSize="16" fontFamily="Inter,sans-serif" fontWeight="700">{r}</text>
          {i < reactants.length - 1 && <text x={x + rGap/2} y={cy+8} textAnchor="middle" fill="#94a3b8" fontSize="20" fontFamily="Inter,sans-serif">+</text>}
        </g>;
      })}
      {/* Arrow */}
      <g>
        <rect x={arrowX-36} y={cy-3} width="72" height="6" rx="3" fill="#475569"/>
        <path d={`M${arrowX+36},${cy-12} L${arrowX+52},${cy} L${arrowX+36},${cy+12}Z`} fill={accentColor}/>
        <line x1={arrowX-34} y1={cy-1} x2={arrowX+34} y2={cy-1} stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
      </g>
      {/* Products */}
      {products.map((p, i) => {
        const x = arrowX + 60 + i * pGap + pGap / 2;
        const id = `product_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={`p${i}`} filter="url(#ce-sh)">
          <circle cx={x+2} cy={cy+3} r="36" fill="rgba(0,0,0,0.3)"/>
          <circle cx={x} cy={cy} r="36" fill="url(#ce-mol)" stroke={hi?'#fff':accentColor} strokeWidth={hi?3:1.5}
            style={{ filter: 'hue-rotate(60deg)' }}/>
          <circle cx={x-10} cy={cy-12} r="6" fill="rgba(255,255,255,0.35)"/>
          <text x={x} y={cy+6} textAnchor="middle" fill="#fff" fontSize="16" fontFamily="Inter,sans-serif" fontWeight="700">{p}</text>
          {i < products.length - 1 && <text x={x + pGap/2} y={cy+8} textAnchor="middle" fill="#94a3b8" fontSize="20" fontFamily="Inter,sans-serif">+</text>}
        </g>;
      })}
    </g>
  );
};
